import { dialogMgr } from 'widgets/toolbox/dialogMgr';
import { scrollWindowTo } from 'widgets/toolbox/scroll';
import { getContentByUrl } from 'widgets/toolbox/ajax';
import { getUrlParams } from 'widgets/toolbox/util';
import { appendParamToURL } from 'widgets/toolbox/util';
import { executeInlineScripts } from 'harmony/toolbox/domUtils';

/**
 * @description Extended HarmonyProductListingMgr implementation
 * @param {ProductListingMgr} ProductListingMgr Base widget for extending
 * @returns {typeof HarmonyProductListingMgr} PLP Manager
 */
export default function (ProductListingMgr) {
    class HarmonyProductListingMgr extends ProductListingMgr {
        prefs() {
            return {
                ...super.prefs(),
                hideVisually: 'h-hide_visually',
                isScrollOnload: false
            };
        }

        init() {
            super.init();
            this.eventBus().on('refinement.update.plp', 'applyFilters');
        }

        applyFilters(url, ref, handleHistory) {
            const alertMessage = this.prefs().accessibilityAlerts.filtersapplied;

            this.updateByUrl(url, ref, handleHistory)
                .then(() => this.accessibilityAlert(alertMessage));

            this.sendApplyFilterEvent(url);
        }

        /**
         * @description Load more content
         * @param {refElement} button Target element
         * @returns {void}
         */
        loadMoreContent(button) {
            const href = button.attr('href');
            let url = '';

            if (typeof href === 'string') {
                url = href;
            }

            this.ref('loadMoreContentBlock').remove();
            this.ref('contentProgressIndicator').remove();
            this.loadChunk(url, response => {
                if (typeof response === 'string') {
                    this.ref('contentGrid').append(response);
                    const accessibilityAlert = this.prefs().accessibilityAlerts.articlesgridupdated;
                    this.eventBus().emit('alert.show', {
                        accessibilityAlert
                    });
                }
            }, false);
        }

        /**
         * @description Load more products
         * @param {refElement} button Target element
         * @returns {void}
         */
        loadMore(button) {
            const href = button.attr('href');
            const loadingText = button.data('text-loading');
            const history = {
                showUrl: button.data('show-url'),
                showAjaxUrl: button.data('show-ajax-url')
            };
            let url = '';

            if (typeof href === 'string') {
                url = href;
            }

            button.setText(loadingText);
            this.ref('loadMoreBlock').addClass(this.prefs().hideVisually);
            this.ref('productProgressIndicator').addClass(this.prefs().hideVisually);

            this.loadChunk(url, response => {
                if (typeof response === 'string') {
                    this.ref('loadMoreBlock').remove();
                    this.ref('productProgressIndicator').remove();
                    this.ref('productGrid').append(response);
                    const accessibilityAlert = this.prefs().accessibilityAlerts.productlistupdated;
                    if ($('.member-only-product').length > 0) {
                        $('.member-only-product').each(function () {
                            $(this).parent().find('.b-product_tile-quantity').hide()
                            $(this).parent().find('.b-product_tile-cta:not(.member-only-product)').hide()
                        })
                    }
                    this.eventBus().emit('alert.show', {
                        accessibilityAlert
                    });
                }
            }, true, history);
        }

        /**
         * @param {string} url Update URL
         * @param {refElement} [ref] Reference element
         * @param {boolean} handleHistory Handle history flag
         * @returns {Promise}
         */
        updateByUrl(url, ref, handleHistory) {
            this.busy();

            const promise = getContentByUrl(url)
                .then(response => {
                    if (typeof response === 'string') {
                        const updateArea = ref || this.ref('self');
                        this.ref('productGrid').empty();
                        dialogMgr.closeDialog();
                        this.render(undefined, undefined, updateArea, response)
                            .then(() => {
                                this.eventBus().emit('plp.updated');

                                if (handleHistory) {
                                    this.handleHistory();
                                    scrollWindowTo(this.ref('grid').get(), true);
                                }

                                executeInlineScripts(updateArea.get());

                                this.has('noResults', () => {
                                    this.handleNoResult();
                                });
                            });
                    }
                }).catch(()=> {
                    this.handleNoResult();
                })
                .finally(() => {
                    this.unbusy();
                });

            this.eventBus().emit('loader.start', promise);

            return promise;
        }

        /**
         * @description Sends GTM event and updateView
         * @param {any} button Target element
         * @returns {void}
        */
        updateView(button) {
            this.sendApplyFilterEvent(button.data('href'));
            super.updateView(button);
        }

        sendApplyFilterEvent(url) {
            /**
             * @description url params object
             * @type {any}
            */
            const urlParams = getUrlParams(url);
            const productListingMgrSelector = this.ref('self').get();

            /* eslint-disable no-prototype-builtins */
            if (urlParams.hasOwnProperty('cgid')) {
                const refinementSelector = productListingMgrSelector.querySelector('[data-category-id="' + urlParams.cgid + '"]');
                urlParams.type = refinementSelector ? refinementSelector.dataset.categoryName : urlParams.cgid;
            }

            this.eventBus().emit('gtm.list.filter', urlParams);
            this.eventBus().emit('monetate.filter.apply', url);
        }

        /**
         * @description Update Grid
         * @param {InstanceType<InputSelect>} select Target element
         * @returns {void}
         */
        updateGrid(select) {
            super.updateGrid(select);
            const selectedSorting = select.getSelectedOptions();
            if (selectedSorting) {
                this.eventBus().emit('gtm.list.sort', selectedSorting.getText().trim());
                this.eventBus().emit('monetate.sort.apply', selectedSorting.data('url'));
            }
        }

        /**
         * @description Loads chunk of data and executes callback. Data might be either products or content
        * @param {string} url load chunk url
        * @param {(arg: Response) => void} successCb success callback to be called after chunk was loaded with response as an argument
        * @param {boolean} handleHistory do we need to handle history change after chunk loaded
        * @param {object} [history] history object
        */
        loadChunk(url, successCb, handleHistory, history) {
            if (!url) {
                console.error('Something is wrong with target URL');
            } else {
                this.busy();
                const promise = getContentByUrl(
                    appendParamToURL(url, 'selectedUrl', url)
                ).then(response => {
                    successCb(response);

                    if (handleHistory) {
                        this.handleHistory(true, history);
                    }

                    this.eventBus().emit('monetate.loadmore', url);
                }).finally(() => {
                    this.unbusy();
                });

                this.eventBus().emit('loader.start', promise);
            }
        }

        handleNoResult() {
            this.has('firstRefinement', ref => {
                ref.focus();
            });
        }
    }

    return HarmonyProductListingMgr;
}
